import { useQuery } from '@tanstack/react-query';
import { useAxios } from '../AxiosHandler';

// Type definitions
interface Breed {
    ID: number;
    NAME: string;
}

interface Dog {
    [key: string]: any;
}

// API functions - these will be called from within the hooks
const createApiFunctions = (axios: any) => ({
    fetchBreeds: async (): Promise<Breed[]> => {
        const response = await axios.single({
            method: 'get',
            url: '/api/breeds/all',
            responseType: 'json',
        });
        return response.data || [];
    },

    fetchAllDogs: async (
        breedId: string,
        filters: any[] = [],
        sorting: { field: string; order: 'asc' | 'desc' } | null = null,
        amount: number = 15,
        skip: number = 0
    ): Promise<{ dogs: Dog[]; total: number }> => {
        if (!breedId) return { dogs: [], total: 0 };
        
        // Build query parameters
        const params = new URLSearchParams();
        if (filters.length > 0) {
            params.append('filters', JSON.stringify(filters));
        }
        if (sorting) {
            params.append('sorting', JSON.stringify(sorting));
        }
        params.append('amount', amount.toString());
        params.append('skip', skip.toString());
        
        const queryString = params.toString();
        const url = `/api/dogs/publicalldogs/${breedId}${queryString ? '?' + queryString : ''}`;
        
        try {
            const response = await axios.single({
                method: 'get',
                url,
                responseType: 'json',
            });
            
            if (response.data && response.data.pagedata) {
                return { 
                    dogs: response.data.pagedata, 
                    total: response.data.totalcount || response.data.pagedata.length 
                };
            }
            
            return { dogs: [], total: 0 };
        } catch (error: any) {
            console.error('Error fetching all dogs:', error);
            // Return empty result on error instead of throwing
            return { dogs: [], total: 0 };
        }
    },

    fetchBreedingDogs: async (breedId: string): Promise<Dog[]> => {
        if (!breedId) return [];
        
        const response = await axios.single({
            method: 'get',
            url: `/api/dogs/breedingdogs/${breedId}`,
            responseType: 'json',
        });
        return response.data || [];
    },

    fetchBreedregSchema: async (breedId: string): Promise<any> => {
        if (!breedId) return null;
        
        const schemaResponse = await axios.single({
            method: 'get',
            url: `/api/dynschema/special/${breedId}/malepartnerreq/${breedId}`,
            responseType: 'json',
        });
        
        const schema = schemaResponse.data;
        
        // Fetch questions if schema exists and has an ID
        if (schema && schema.ID) {
            try {
                const questionsResponse = await axios.single({
                    method: 'get',
                    url: `/api/dynschema/${breedId}/${schema.ID}`,
                    responseType: 'json',
                });
                
                return {
                    ...schema,
                    questions: questionsResponse.data || []
                };
            } catch (error) {
                console.error('Error fetching schema questions:', error);
                // Return schema without questions if fetch fails
                return schema;
            }
        }
        
        return schema;
    },

    simulateBreeding: async (dogId: string, partnerIds: string[]): Promise<any[]> => {
        if (!dogId || !partnerIds.length) return [];
        
        const response = await axios.single({
            method: 'post',
            url: '/api/dogs/publicsimulatebreeding',
            data: {
                dogId,
                partnerIds
            },
            responseType: 'json',
        });
        return response.data || [];
    }
});

// Custom hooks
export const useBreeds = () => {
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const apiFunctions = createApiFunctions(axios);
    
    return useQuery<Breed[]>({
        queryKey: ['breeds'],
        queryFn: apiFunctions.fetchBreeds,
        staleTime: 10 * 60 * 1000, // 10 minutes - breeds don't change often
        gcTime: 30 * 60 * 1000, // 30 minutes (renamed from cacheTime)
    });
};

export const useAllDogs = (
    breedId: string,
    filters: any[] = [],
    sorting: { field: string; order: 'asc' | 'desc' } | null = null,
    amount: number = 15,
    skip: number = 0
) => {
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const apiFunctions = createApiFunctions(axios);
    
    return useQuery<{ dogs: Dog[]; total: number }>({
        queryKey: ['allDogs', breedId, filters, sorting, amount, skip],
        queryFn: () => apiFunctions.fetchAllDogs(breedId, filters, sorting, amount, skip),
        enabled: !!breedId && !!breedId.trim() && filters.length > 0, // Only run query if breedId exists and filters are provided
        staleTime: 30 * 1000, // 30 seconds - more responsive to filter/sort changes
        gcTime: 5 * 60 * 1000, // 5 minutes
        retry: 1, // Only retry once on error
        retryDelay: 1000, // Wait 1 second before retrying
    });
};

export const useBreedingDogs = (breedId: string, enabled: boolean = true) => {
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const apiFunctions = createApiFunctions(axios);
    
    return useQuery<Dog[]>({
        queryKey: ['breedingDogs', breedId],
        queryFn: () => apiFunctions.fetchBreedingDogs(breedId),
        enabled: !!breedId && enabled, // Only run query if breedId exists and enabled is true
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime)
    });
};

export const useBreedregSchema = (breedId: string) => {
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const apiFunctions = createApiFunctions(axios);
    
    return useQuery<any>({
        queryKey: ['breedregSchema', breedId],
        queryFn: () => apiFunctions.fetchBreedregSchema(breedId),
        enabled: !!breedId, // Only run query if breedId exists
        staleTime: 10 * 60 * 1000, // 10 minutes - schemas don't change often
        gcTime: 30 * 60 * 1000, // 30 minutes (renamed from cacheTime)
    });
};

export const useSimulateBreeding = (dogId: string, partnerIds: string[]) => {
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const apiFunctions = createApiFunctions(axios);
    
    return useQuery<any[]>({
        queryKey: ['simulateBreeding', dogId, partnerIds],
        queryFn: () => apiFunctions.simulateBreeding(dogId, partnerIds),
        enabled: !!dogId && !!partnerIds.length, // Only run if both dogId and partnerIds exist
        staleTime: 2 * 60 * 1000, // 2 minutes - breeding simulations might change
        gcTime: 5 * 60 * 1000, // 5 minutes
    });
};
